/**
 * ConfigSection Component
 *
 * Collapsible section wrapper for configuration settings
 */

import React from 'https://esm.sh/react@18.2.0';
import { Icon } from '../common/index.js';
import { useResponsiveLayout } from '../../hooks/index.js';

const { createElement: h, useState, useEffect, useRef } = React;

/**
 * ConfigSection component
 * @param {string} title - Section title
 * @param {string} description - Section description
 * @param {boolean} defaultOpen - Default open state
 * @param {boolean} open - Controlled open state (overrides internal state)
 * @param {function} onToggle - Callback when toggled (for controlled mode)
 * @param {ReactNode} children - Section content
 */
const ConfigSection = ({ title, description, defaultOpen = true, open, onToggle, warning, icon, badge, children }) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const sectionRef = useRef(null);
  const userToggledRef = useRef(false);
  const { isMobile } = useResponsiveLayout();

  // Use controlled state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;

  const handleToggle = () => {
    const willOpen = !isOpen;
    if (willOpen) userToggledRef.current = true;
    if (onToggle) {
      // Controlled mode - call parent callback
      onToggle(willOpen);
    } else {
      // Uncontrolled mode - update internal state
      setInternalOpen(willOpen);
    }
  };

  // Auto-scroll to section header on mobile when user opens it.
  // setTimeout gives the browser enough time to fully lay out expanded content
  // before calculating the scroll position (rAF alone isn't sufficient when
  // newly-rendered content stretches the page height).
  useEffect(() => {
    if (isOpen && userToggledRef.current && sectionRef.current && isMobile) {
      userToggledRef.current = false;
      const timer = setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return h('div', {
    ref: sectionRef,
    className: 'border border-gray-200 dark:border-gray-700 rounded-lg mb-4 overflow-hidden scroll-mt-14'
  },
    // Header
    h('button', {
      type: 'button',
      onClick: handleToggle,
      className: 'w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors gap-3'
    },
      icon && h(Icon, { name: icon, size: 22, className: 'text-gray-500 dark:text-gray-400 flex-shrink-0' }),
      h('div', { className: 'text-left flex-1 min-w-0' },
        h('h3', { className: 'text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center flex-wrap gap-2' },
          title,
          warning && h(Icon, { name: 'alertTriangle', size: 16, className: 'text-amber-500' }),
          badge
        ),
        description && h('p', { className: 'text-sm text-gray-500 dark:text-gray-400 mt-0.5' }, description)
      ),
      h(Icon, {
        name: isOpen ? 'chevronUp' : 'chevronDown',
        size: 20,
        className: 'text-gray-500 dark:text-gray-400 flex-shrink-0'
      })
    ),
    // Content
    isOpen && h('div', { className: 'p-4 bg-white dark:bg-gray-900' }, children)
  );
};

export default ConfigSection;
